import React from 'react';
import { ChevronDown } from 'lucide-react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { badgeVariants } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type QualificationStatusValue = 'qualified' | 'unqualified' | 'unknown';

const qualificationOptions: Array<{ value: QualificationStatusValue; label: string }> = [
  { value: 'qualified', label: 'Qualified' },
  { value: 'unqualified', label: 'Unqualified' },
  { value: 'unknown', label: 'Pending Review' },
];

const qualificationLabelMap = qualificationOptions.reduce<Record<QualificationStatusValue, string>>((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {} as Record<QualificationStatusValue, string>);

const normalizeQualification = (value?: string) => (value ? value.toLowerCase() : '');

const getQualificationBadgeVariant = (status?: string) => {
  if (!status) return 'secondary';
  const normalized = status.toLowerCase();
  if (normalized === 'qualified') return 'default';
  if (normalized === 'unqualified') return 'destructive';
  if (normalized === 'unknown') return 'secondary';
  return 'outline';
};

interface QualificationStatusSelectProps {
  value?: string;
  onChange?: (value: QualificationStatusValue) => void;
  align?: 'start' | 'center' | 'end';
  className?: string;
  disabled?: boolean;
  triggerType?: 'button' | 'span';
}

export const QualificationStatusSelect: React.FC<QualificationStatusSelectProps> = ({
  value,
  onChange,
  align = 'end',
  className,
  disabled = false,
  triggerType = 'button',
}) => {
  const normalized = normalizeQualification(value);
  const displayLabel =
    (normalized && (qualificationLabelMap as Record<string, string>)[normalized]) || 'N/A';
  const radioValue =
    normalized && (qualificationLabelMap as Record<string, string>)[normalized] ? normalized : '';

  const TriggerComponent = triggerType === 'span' ? 'span' : 'button';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <TriggerComponent
          {...(triggerType === 'button'
            ? { type: 'button' }
            : { role: 'button', tabIndex: disabled ? -1 : 0 })}
          className={cn(
            badgeVariants({ variant: getQualificationBadgeVariant(normalized) }),
            'inline-flex items-center gap-1 capitalize hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            disabled && 'cursor-not-allowed opacity-70',
            className
          )}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
          }}
          onKeyDown={(event: React.KeyboardEvent) => {
            event.stopPropagation();
          }}
        >
          <span>{displayLabel}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </TriggerComponent>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} sideOffset={6}>
        <DropdownMenuRadioGroup
          value={radioValue}
          onValueChange={(nextValue) => {
            if (onChange) {
              onChange(nextValue as QualificationStatusValue);
            }
          }}
        >
          {qualificationOptions.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              className="capitalize"
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default QualificationStatusSelect;
